import React, { Dispatch, SetStateAction } from "react";
import LineChart from "./LineChart";
import PieChart from "./PieChart";
import Table from "./Table";
import { IComplaint, ICooperative } from "../../exports/interfaces";
import { formatDate } from "../../exports/functions";
import { useOption } from "../../context/OptionContext";

const Dashboard = ({
  cooperative,
  complaint,
  setCooperative,
}: {
  cooperative: ICooperative[];
  complaint: IComplaint[];
  setCooperative: Dispatch<SetStateAction<ICooperative[]>>;
}) => {
  const { categories, statusOptions } = useOption();

  const now = new Date();

  const registerCount = cooperative.length;
  const registerThisMonth = cooperative.filter((n) => {
    const date = new Date(n.createdAt);

    return (
      date.getMonth() === now.getMonth() &&
      date.getFullYear() === now.getFullYear()
    );
  }).length;
  const lastUpdated = cooperative.length
    ? new Date(
        Math.max(...cooperative.map((n) => new Date(n.createdAt).getTime())),
      )
    : null;
  const natureOfWork = categories.find(
    (item) => item.name === "Nature of Work / Notice Audience",
  );

  const natureOfWorkStats =
    natureOfWork?.dropdowns.slice(0, 4).map((dropdown) => ({
      label: dropdown.label,
      count: cooperative.filter((item) => item.natureOfWork === dropdown.label)
        .length,
    })) ?? [];

  const activeCooperativesCount = cooperative.filter(
    (item) => item.status?.toLowerCase() === "active",
  ).length;

  const complaintStatuses = statusOptions.find(
    (item) => item.name === "Complaint Statuses",
  );

  const chartData =
    complaintStatuses?.statusOptions.map((status) => ({
      label: status.label,
      value: complaint.filter((item) => item.status === status.label).length,
      color: status.color,
    })) ?? [];

  const visibleItems = chartData.slice(0, 5);
  const remainingCount = chartData.length - visibleItems.length;

  return (
    <>
      <h3 className="mb-3">Welcome user!</h3>

      <div className="row g-2">
        <div className="col-md-3">
          <div
            id="total-registered"
            className="dashboard-card h-100 d-flex flex-column"
          >
            <div className="card-body">
              <p className="count-card-heading">
                Total Registered Cooperatives
              </p>
              <h2>{registerCount.toString().padStart(2, "0")}</h2>
            </div>
            <div className="text-end">
              <small>Last Updated: {formatDate(lastUpdated)}</small>
            </div>
          </div>
        </div>

        <div className="col-md-6">
          <div
            id="types-of-cooperatives"
            className="dashboard-card h-100 d-flex flex-column"
          >
            <div className="card-body">
              <p className="mb-1 count-card-heading">Types of Cooperatives</p>
              <div className="row g-3 mb-2">
                {natureOfWorkStats.map((item) => (
                  <div className="col-md-3 col-6" key={item.label}>
                    <p>{item.label}</p>
                    <h2 className="my-0">
                      {item.count.toString().padStart(2, "0")}
                    </h2>
                  </div>
                ))}
              </div>
            </div>
            <div className="text-end">
              <small>Last Updated: {formatDate(lastUpdated)}</small>
            </div>
          </div>
        </div>

        <div className="col-md-3">
          <div
            id="onboarding-applicants"
            className="dashboard-card h-100 d-flex flex-column"
          >
            <div className="card-body">
              <p className="count-card-heading">Onboarding Applicants</p>
              <h2>{activeCooperativesCount.toString().padStart(2, "0")}</h2>
            </div>
            <div className="text-end">
              <small>Last Updated: {formatDate(lastUpdated)}</small>
            </div>
          </div>
        </div>
      </div>
      <div className="row g-2 dashboard-section">
        <div className="col-lg-9">
          <div id="registration-statistics" className="dashboard-card h-100">
            <div className="card-body">
              <h6>REGISTRATION STATISTICS</h6>
              <div className="row">
                <div className="col-md-9">
                  <LineChart cooperative={cooperative} />
                </div>
                <div className="col-md-3 d-flex align-items-center justify-content-center">
                  <div className="dashboard-card inner-dashboard-card">
                    <div className="card-body text-center">
                      <p>This month</p>
                      <h2>{registerThisMonth.toString().padStart(2, "0")}</h2>
                      <p>Total registered</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-lg-3">
          <div id="complaint-statistics" className="dashboard-card h-100">
            <div className="card-body">
              <h6>COMPLAINT STATISTICS</h6>
              <div className="text-center">
                <PieChart data={chartData} width={180} height={180} />
              </div>
              <div className="mt-3 d-flex flex-wrap justify-content-center">
                <div className="mt-3 d-flex flex-wrap">
                  {visibleItems.map((item) => (
                    <div
                      key={item.label}
                      className="mb-2 px-2"
                      style={{ width: "50%" }}
                    >
                      <div className="d-flex align-items-center justify-content-between">
                        <div
                          className="d-flex align-items-center gap-2"
                          style={{ minWidth: 0 }}
                        >
                          <span
                            style={{
                              width: 10,
                              height: 10,
                              borderRadius: "50%",
                              backgroundColor: item.color,
                              flexShrink: 0,
                            }}
                          />
                          <small
                            style={{
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {item.label}
                          </small>
                        </div>

                        <small>{item.value.toString().padStart(2, "0")}</small>
                      </div>
                    </div>
                  ))}

                  {remainingCount > 0 && (
                    <div className="mb-2 px-2" style={{ width: "50%" }}>
                      <div className="d-flex align-items-center justify-content-center h-100">
                        <small className="fw-bold">+ {remainingCount}</small>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-12">
          <Table cooperative={cooperative} setCooperative={setCooperative} />
        </div>
      </div>
    </>
  );
};

export default Dashboard;
